import { parseErnMap } from '../../tarifs/model/manifest-form';

/**
 * A single tarif's `refs` block, split into the pins the console can offer and everything else.
 *
 * `insuranceCore` is the one ref the manifest schema REQUIRES; `tarifDeckung` and `leadLifecycle`
 * (SDD-013 §6.1) are the two optional owned pins it declares beside it. All three name assets of
 * THIS tarif, which the console has already read to draw the asset lists — so asking an operator
 * to type an ERN the console could look up is a transcription error waiting to happen, and the
 * gate would refuse it with a 409 several screens later.
 *
 * Everything else stays a text block, for the reason the group refs field keeps one: the schema
 * allows further refs beside these, and a picker cannot be built for candidates that live in
 * another service's catalog.
 */
export interface TarifRefs {
  readonly insuranceCore: string;
  readonly tarifDeckung: string;
  /** Empty means "the platform default machine" (SDD-013 §6.1), never an empty line. */
  readonly leadLifecycle: string;
  /** Empty means "the platform default billing rules" (SDD-015 §5.5, B-7), never an empty line. */
  readonly billingConfig: string;
  /** The remaining `key = ern` lines, verbatim. */
  readonly other: string;
}

const PINNED = new Set(['insuranceCore', 'tarifDeckung', 'leadLifecycle', 'billingConfig']);

export function splitTarifRefs(refs: string): TarifRefs {
  const { entries } = parseErnMap(refs);
  const other = Object.keys(entries)
    .filter((key) => !PINNED.has(key))
    .sort((a, b) => a.localeCompare(b))
    .map((key) => `${key} = ${String(entries[key])}`)
    .join('\n');

  return {
    insuranceCore: entries.insuranceCore ?? '',
    tarifDeckung: entries.tarifDeckung ?? '',
    leadLifecycle: entries.leadLifecycle ?? '',
    billingConfig: entries.billingConfig ?? '',
    other,
  };
}

/**
 * Back to a refs block.
 *
 * An unset pin is OMITTED rather than written as `insuranceCore = `, for the reason
 * `joinGroupRefs` omits one: both make the manifest invalid, but the empty form is a malformed
 * LINE — a typing mistake in this editor — while the missing key is what the schema names, at
 * `refs.insuranceCore`, which is the path the wizard turns into a message under the picker.
 * Writing the empty line would produce two complaints about one hole, one of them pointing at a
 * mistake the operator did not make.
 */
export function joinTarifRefs({ insuranceCore, tarifDeckung, leadLifecycle, billingConfig, other }: TarifRefs): string {
  const lines = [
    ...(insuranceCore === '' ? [] : [`insuranceCore = ${insuranceCore}`]),
    ...(tarifDeckung === '' ? [] : [`tarifDeckung = ${tarifDeckung}`]),
    ...(leadLifecycle === '' ? [] : [`leadLifecycle = ${leadLifecycle}`]),
    ...(billingConfig === '' ? [] : [`billingConfig = ${billingConfig}`]),
    ...other.split('\n').filter((line) => line.trim() !== ''),
  ];
  return lines.join('\n');
}
